import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { sendTransactionalEmail } from "@/lib/email/send";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, XCircle, Clock, Mail, Phone, Instagram, MapPin, Search, Trash2, Send, Users, BarChart3, User, KeyRound } from "lucide-react";
import { zodiacLabel } from "@/lib/zodiac";
import { adminDeleteUser } from "@/lib/admin-users.functions";
import { useServerFn } from "@tanstack/react-start";

export const Route = createFileRoute("/harikaradmin/")({
  head: () => ({ meta: [{ title: "Admin — JUËN" }] }),
  component: AdminPage,
});

type Status = "pending" | "approved" | "rejected";

interface Member {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  birth_date: string | null;
  instagram: string | null;
  city: string | null;
  bio: string | null;
  gender: string | null;
  membership_status: Status;
  created_at: string;
}

const GENDER_LABEL: Record<string, string> = {
  female: "Kadın",
  male: "Erkek",
};

interface Stats {
  pending: number;
  approved: number;
  rejected: number;
  total: number;
}

function AdminPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState<Member[]>([]);
  const [filter, setFilter] = useState<Status>("pending");
  const [loadingList, setLoadingList] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [stats, setStats] = useState<Stats>({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [cityFilter, setCityFilter] = useState<string>("");
  const [genderFilter, setGenderFilter] = useState<string>("");
  const [under21Only, setUnder21Only] = useState<boolean>(false);
  const deleteUserFn = useServerFn(adminDeleteUser);

  useEffect(() => {
    if (!loading) {
      if (!user) void navigate({ to: "/login" });
      else if (!isAdmin) void navigate({ to: "/dashboard" });
    }
  }, [loading, user, isAdmin, navigate]);

  const load = async () => {
    setLoadingList(true);
    const PAGE = 1000;
    const all: Member[] = [];
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("membership_status", filter)
        .order("created_at", { ascending: false })
        .range(from, from + PAGE - 1);
      if (error) {
        toast.error(error.message);
        break;
      }
      const batch = (data as Member[]) ?? [];
      all.push(...batch);
      if (batch.length < PAGE) break;
    }
    setMembers(all);
    setLoadingList(false);
  };


  const loadStats = async () => {
    const statuses: Status[] = ["pending", "approved", "rejected"];
    const counts: Partial<Stats> = {};
    let total = 0;
    for (const s of statuses) {
      const { count } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true })
        .eq("membership_status", s);
      counts[s] = count ?? 0;
      total += count ?? 0;
    }
    setStats({ ...(counts as Stats), total });
  };

  useEffect(() => {
    if (isAdmin) {
      void load();
      void loadStats();
    }
    setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, filter]);

  const ageOf = (birth: string | null): number | null => {
    if (!birth) return null;
    const d = new Date(birth);
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age;
  };

  const filtered = members.filter((m) => {
    if (cityFilter && m.city !== cityFilter) return false;
    if (genderFilter && m.gender !== genderFilter) return false;
    if (under21Only) {
      const age = ageOf(m.birth_date);
      if (age === null || age >= 21) return false;
    }
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      m.first_name.toLowerCase().includes(q) ||
      m.last_name.toLowerCase().includes(q) ||
      m.email.toLowerCase().includes(q) ||
      (m.phone ?? "").toLowerCase().includes(q) ||
      (m.instagram ?? "").toLowerCase().includes(q)
    );
  });

  const under21Count = useMemo(
    () => members.filter((m) => {
      const age = ageOf(m.birth_date);
      return age !== null && age < 21;
    }).length,
    [members],
  );

  const cityDistribution = useMemo(() => {
    const map = new Map<string, number>();
    for (const m of members) {
      if (!m.city) continue;
      map.set(m.city, (map.get(m.city) ?? 0) + 1);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, [members]);

  const genderDistribution = useMemo(() => {
    const map = new Map<string, number>();
    for (const m of members) {
      if (!m.gender) continue;
      map.set(m.gender, (map.get(m.gender) ?? 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [members]);

  const toggleSelect = (id: string) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((m) => m.id)));
  };

  const bulkApprove = async () => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    const ids = Array.from(selected);
    const targets = members.filter((m) => ids.includes(m.id));
    let ok = 0;
    for (const m of targets) {
      const { error } = await supabase.rpc("admin_review_membership", {
        _user_id: m.id,
        _status: "approved",
      });
      if (!error) {
        ok++;
        if (m.email) {
          try {
            await sendTransactionalEmail({
              templateName: "membership-approved",
              recipientEmail: m.email,
              idempotencyKey: `membership-approved-${m.id}`,
              templateData: { firstName: m.first_name },
            });
          } catch (e) {
            console.error("approval email failed", e);
          }
        }
      }
    }
    setBulkLoading(false);
    toast.success(`${ok} başvuru onaylandı`);
    setSelected(new Set());
    void load();
  };

  const updateStatus = async (id: string, status: Status) => {
    const member = members.find((m) => m.id === id);
    const { error } = await supabase.rpc("admin_review_membership", {
      _user_id: id,
      _status: status,
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(status === "approved" ? "Üyelik onaylandı" : "Başvuru reddedildi");

    if (status === "approved" && member?.email) {
      try {
        await sendTransactionalEmail({
          templateName: "membership-approved",
          recipientEmail: member.email,
          idempotencyKey: `membership-approved-${id}`,
          templateData: { firstName: member.first_name },
        });
      } catch (e) {
        console.error("approval email failed", e);
      }
    }
    void load();
  };

  const resendApprovalEmail = async (member: Member) => {
    if (!member.email) {
      toast.error("Bu üyenin e-posta adresi yok");
      return;
    }
    try {
      await sendTransactionalEmail({
        templateName: "membership-approved",
        recipientEmail: member.email,
        idempotencyKey: `membership-approved-resend-${member.id}-${Date.now()}`,
        templateData: { firstName: member.first_name },
      });
      toast.success(`Onay maili tekrar gönderildi: ${member.email}`);
    } catch (e) {
      console.error("resend approval email failed", e);
      toast.error("Mail gönderilemedi");
    }
  };

  const sendPasswordReset = async (member: Member) => {
    if (!member.email) {
      toast.error("Bu üyenin e-posta adresi yok");
      return;
    }
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(member.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.success(`Şifre sıfırlama maili gönderildi: ${member.email}`);
    } catch (e: any) {
      console.error("password reset failed", e);
      toast.error(e?.message ?? "Mail gönderilemedi");
    }
  };



  const deleteUser = async (id: string) => {
    const member = members.find((m) => m.id === id);
    if (!member) return;
    if (!confirm(`${member.first_name} ${member.last_name} (${member.email}) tamamen silinecek. Emin misiniz?`)) return;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        toast.error("Oturum doğrulanamadı");
        return;
      }

      const result = await deleteUserFn({ data: { userId: id, accessToken } });
      if (!result.success) {
        toast.error(result.error ?? "Silinemedi");
        return;
      }
      toast.success("Kullanıcı silindi");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Silinemedi");
    }
  };

  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  const tabs: { key: Status; label: string; icon: typeof Clock }[] = [
    { key: "pending", label: "Bekleyen", icon: Clock },
    { key: "approved", label: "Onaylı", icon: CheckCircle2 },
    { key: "rejected", label: "Reddedilen", icon: XCircle },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Admin Panel</h1>
          <p className="mt-2 text-muted-foreground">Üyelik başvurularını inceleyin.</p>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: "Bekleyen", value: stats.pending, icon: Clock, color: "text-amber-500" },
            { label: "Onaylı", value: stats.approved, icon: CheckCircle2, color: "text-emerald-500" },
            { label: "Reddedilen", value: stats.rejected, icon: XCircle, color: "text-red-500" },
            { label: "Toplam", value: stats.total, icon: Users, color: "text-primary" },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="rounded-xl border border-border bg-card p-4 shadow-card">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon className={`h-4 w-4 ${s.color}`} />
                  {s.label}
                </div>
                <div className="mt-1 text-2xl font-bold">{s.value}</div>
              </div>
            );
          })}
        </div>

        <AdminTabs />

        <div className="mb-6 flex gap-2 rounded-xl border border-border bg-card p-1 shadow-card">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = filter === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                  active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {cityDistribution.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Şehir dağılımı:</span>
            {cityDistribution.map(([city, count]) => (
              <button
                key={city}
                onClick={() => setCityFilter(cityFilter === city ? "" : city)}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  cityFilter === city
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {city} <span className="opacity-70">({count})</span>
              </button>
            ))}
            {cityFilter && (
              <button
                onClick={() => setCityFilter("")}
                className="text-xs text-muted-foreground underline hover:text-foreground"
              >
                Filtreyi kaldır
              </button>
            )}
          </div>
        )}

        {genderDistribution.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Cinsiyet dağılımı:</span>
            {genderDistribution.map(([gender, count]) => (
              <button
                key={gender}
                onClick={() => setGenderFilter(genderFilter === gender ? "" : gender)}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  genderFilter === gender
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {GENDER_LABEL[gender] ?? gender} <span className="opacity-70">({count})</span>
              </button>
            ))}
            {genderFilter && (
              <button
                onClick={() => setGenderFilter("")}
                className="text-xs text-muted-foreground underline hover:text-foreground"
              >
                Filtreyi kaldır
              </button>
            )}
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setUnder21Only((v) => !v)}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              under21Only
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            21 yaş altı <span className="opacity-70">({under21Count})</span>
          </button>
        </div>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Ad, e-posta, telefon, instagram..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          {filter === "pending" && filtered.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={toggleSelectAll}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                {selected.size === filtered.length ? "Seçimi kaldır" : "Tümünü seç"}
              </button>
              <Button
                onClick={bulkApprove}
                disabled={selected.size === 0 || bulkLoading}
                size="sm"
                className="bg-success text-success-foreground hover:bg-success/90"
              >
                <CheckCircle2 className="mr-1.5 h-4 w-4" />
                {bulkLoading ? "Onaylanıyor..." : `Seçilenleri onayla (${selected.size})`}
              </Button>
            </div>
          )}
        </div>

        {loadingList ? (
          <div className="py-16 text-center text-muted-foreground">Yükleniyor...</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">
            {members.length === 0 ? "Bu kategoride başvuru yok." : "Aramaya uyan başvuru yok."}
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((m) => (
              <MemberCard
                key={m.id}
                member={m}
                onUpdate={updateStatus}
                onDelete={deleteUser}
                onResendApproval={resendApprovalEmail}
                onPasswordReset={sendPasswordReset}
                selectable={filter === "pending"}
                selected={selected.has(m.id)}
                onToggleSelect={() => toggleSelect(m.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MemberCard({
  member,
  onUpdate,
  onDelete,
  onResendApproval,
  onPasswordReset,
  selectable = false,
  selected = false,
  onToggleSelect,
}: {
  member: Member;
  onUpdate: (id: string, status: Status) => void;
  onDelete: (id: string) => void;
  onResendApproval: (member: Member) => void;
  onPasswordReset: (member: Member) => void;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {selectable && (
            <Checkbox
              checked={selected}
              onCheckedChange={() => onToggleSelect?.()}
              className="mt-1"
            />
          )}
          <div>
          <Link
            to="/harikaradmin/members/$userId"
            params={{ userId: member.id }}
            className="text-lg font-semibold hover:underline"
          >
            {member.first_name} {member.last_name}
          </Link>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{member.email}</span>
            {member.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{member.phone}</span>}
            {member.instagram && (
              <a
                href={`https://instagram.com/${member.instagram.replace(/^@/, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                referrerPolicy="no-referrer"
                className="inline-flex items-center gap-1.5 hover:text-foreground hover:underline"
              >
                <Instagram className="h-3.5 w-3.5" />{member.instagram}
              </a>
            )}
            {member.city && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{member.city}</span>}
          </div>
          {(member.birth_date || member.gender) && (
            <p className="mt-1 text-sm text-muted-foreground">
              {member.birth_date && <>Doğum: {new Date(member.birth_date).toLocaleDateString("tr-TR")}</>}
              {member.birth_date && <> · Burç: {zodiacLabel(member.birth_date)}</>}
              {member.birth_date && member.gender ? " · " : ""}
              {member.gender && <>Cinsiyet: {GENDER_LABEL[member.gender] ?? member.gender}</>}
            </p>
          )}
          {member.bio && <p className="mt-3 max-w-2xl text-sm">{member.bio}</p>}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {member.membership_status === "pending" && (
            <>
              <Button onClick={() => onUpdate(member.id, "approved")} size="sm" className="bg-success text-success-foreground hover:bg-success/90">
                <CheckCircle2 className="mr-1.5 h-4 w-4" />Onayla
              </Button>
              <Button onClick={() => onUpdate(member.id, "rejected")} size="sm" variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/10">
                <XCircle className="mr-1.5 h-4 w-4" />Reddet
              </Button>
            </>
          )}
          {member.membership_status === "approved" && (
            <>
              <Button onClick={() => onResendApproval(member)} size="sm" variant="outline">
                <Send className="mr-1.5 h-4 w-4" />Maili yeniden gönder
              </Button>
              <Button onClick={() => onPasswordReset(member)} size="sm" variant="outline">
                <KeyRound className="mr-1.5 h-4 w-4" />Şifre sıfırlama maili
              </Button>
              <Button onClick={() => onUpdate(member.id, "rejected")} size="sm" variant="outline">İptal et</Button>
            </>
          )}

          {member.membership_status === "rejected" && (
            <Button onClick={() => onUpdate(member.id, "approved")} size="sm" variant="outline">Onayla</Button>
          )}
          <Button onClick={() => onDelete(member.id)} size="sm" variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/10">
            <Trash2 className="mr-1.5 h-4 w-4" />Sil
          </Button>
        </div>
      </div>
    </div>
  );
}

